import { Link, Meta, Title } from "@solidjs/meta";
import { A, useParams } from "@solidjs/router";
import { Show } from "solid-js";
import { BlogNotFound, LabelTabs, PostGrid } from "~/components/marketing/BlogCards";
import { getAppBaseUrl } from "~/lib/getAppBaseUrl";
import { getSocialCardImageUrl } from "~/lib/socialCard";
import { labelToSlug, normalizeLabels } from "./blogLabels";
import { visiblePosts } from "./posts";

export default function BlogLabelRoute() {
  const params = useParams<{ label: string }>();
  const labelSlug = () => params.label;

  const matchingPosts = () =>
    visiblePosts.filter((post) => normalizeLabels(post.labels).some((label) => labelToSlug(label) === labelSlug()));

  const labelTitle = () =>
    matchingPosts()
      .flatMap((post) => normalizeLabels(post.labels))
      .find((label) => labelToSlug(label) === labelSlug());

  // Every /blog/<slug> that is not a post lands here, so a label no visible post carries is
  // a mistyped URL: it gets the 404, not an empty label page.
  return (
    <Show when={labelTitle()} fallback={<BlogNotFound />}>
      {(title) => (
        <div class="mx-auto flex w-full max-w-280 flex-col gap-10 px-4 pt-12 pb-20 sm:px-8 sm:pt-14">
          <Title>{`${title()} | Chief Blog`}</Title>
          <Meta name="description" content={`Chief blog posts labelled ${title()}.`} />
          <Meta property="og:title" content={`${title()} | Chief Blog`} />
          <Meta property="og:description" content={`Chief blog posts labelled ${title()}.`} />
          <Meta property="og:type" content="website" />
          <Meta property="og:url" content={`${getAppBaseUrl()}/blog/${labelSlug()}`} />
          <Meta property="og:image" content={getSocialCardImageUrl(getAppBaseUrl())} />
          <Meta name="twitter:image" content={getSocialCardImageUrl(getAppBaseUrl())} />
          <Link rel="canonical" href={`${getAppBaseUrl()}/blog/${labelSlug()}`} />
          <div class="flex flex-col gap-2.5">
            <A
              href="/blog"
              class="self-start text-sm text-secondary-text no-underline transition-colors hover:text-primary-text"
            >
              ← Blog
            </A>
            <h1 class="text-4xl font-bold tracking-tight text-primary-text sm:text-5xl">{title()}</h1>
          </div>

          <LabelTabs posts={visiblePosts} active={labelSlug()} />
          <PostGrid posts={matchingPosts()} />
        </div>
      )}
    </Show>
  );
}
